import { Request, Response, NextFunction } from 'express';
import { ZodSchema, ZodError } from 'zod';

// Extend Express Request type to include user property
declare global {
    namespace Express {
        interface Request {
            user?: {
                id: string;
                email: string;
                roles?: string[];
            };
        }
    }
}

/**
 * Validation Middleware Factory
 * 
 * Creates Express middleware that validates requests against Zod schemas.
 * Enforces the API boundary by rejecting invalid input for DDD operations.
 */
export function validateRequest(schema: {
    body?: ZodSchema;
    query?: ZodSchema;
    params?: ZodSchema;
}) {
    return (req: Request, res: Response, next: NextFunction) => {
        try {
            // Validate request body
            if (schema.body) {
                req.body = schema.body.parse(req.body);
            }

            // Validate query parameters
            if (schema.query) {
                req.query = schema.query.parse(req.query);
            }

            // Validate route parameters
            if (schema.params) {
                req.params = schema.params.parse(req.params);
            }

            next();
        } catch (error) {
            if (error instanceof ZodError) {
                return res.status(400).json({
                    error: 'Validation Error',
                    message: 'DDD operation validation failed',
                    details: error.errors.map(err => ({
                        field: err.path.join('.'),
                        message: err.message,
                        code: err.code,
                        received: (err as any).received
                    })),
                    timestamp: new Date().toISOString(),
                    requestId: req.headers['x-request-id'] || 'unknown'
                });
            }

            // Handle other validation errors
            return res.status(400).json({
                error: 'Bad Request',
                message: 'Invalid request format for DDD operation',
                timestamp: new Date().toISOString(),
                requestId: req.headers['x-request-id'] || 'unknown'
            });
        }
    };
}

/**
 * Parameter Validation Schemas for DDD Operations
 */
import { z } from 'zod';

export const DomainParamsSchema = z.object({
    domainId: z.string().uuid('Invalid domain ID format')
});

export const BoundedContextParamsSchema = z.object({
    contextId: z.string().uuid('Invalid bounded context ID format')
});

export const AggregateParamsSchema = z.object({
    aggregateId: z.string().uuid('Invalid aggregate ID format')
});

export const ProjectParamsSchema = z.object({
    projectId: z.string().uuid('Invalid project ID format')
});

export const DomainContextParamsSchema = z.object({
    domainId: z.string().uuid('Invalid domain ID format'),
    contextId: z.string().uuid('Invalid bounded context ID format')
});

export const ContextAggregateParamsSchema = z.object({
    contextId: z.string().uuid('Invalid bounded context ID format'),
    aggregateId: z.string().uuid('Invalid aggregate ID format')
});

/**
 * Common validation middleware instances
 */
export const validateDomainParams = validateRequest({
    params: DomainParamsSchema
});

export const validateBoundedContextParams = validateRequest({
    params: BoundedContextParamsSchema
});

export const validateAggregateParams = validateRequest({
    params: AggregateParamsSchema
});

export const validateProjectParams = validateRequest({
    params: ProjectParamsSchema
});

export const validateDomainContextParams = validateRequest({
    params: DomainContextParamsSchema
});

export const validateContextAggregateParams = validateRequest({
    params: ContextAggregateParamsSchema
});

/**
 * Error Response Helper
 * 
 * Standardizes error responses for DDD operations
 */
export function createErrorResponse(
    error: string,
    message: string,
    statusCode: number = 400,
    details?: any[]
) {
    return {
        error,
        message,
        details,
        timestamp: new Date().toISOString(),
        domain: 'DDD Operations'
    };
}

/**
 * Authentication Middleware for DDD Operations
 * 
 * Ensures proper authentication for domain design operations
 */
export function requireAuthentication(req: Request, res: Response, next: NextFunction) {
    const authHeader = req.headers.authorization;
    
    if (!authHeader || !authHeader.startsWith('Bearer ')) {
        return res.status(401).json(createErrorResponse(
            'Authentication Required',
            'DDD operations require valid authentication',
            401
        ));
    }

    // Extract user info (this would integrate with your auth system)
    const token = authHeader.substring(7);
    
    // For now, we'll use a simple approach (would be replaced with actual token validation)
    if (!token || token === 'invalid') {
        return res.status(401).json(createErrorResponse(
            'Invalid Token',
            'Authentication token is invalid or expired',
            401
        ));
    }

    // Add user info to request (would come from token validation)
    req.user = {
        id: 'user-123',
        email: 'user@example.com',
        roles: ['domain-expert', 'architect']
    };

    next();
}

/**
 * Authorization Middleware for DDD Operations
 * 
 * Ensures users have proper permissions for domain design operations
 */
export function requireDomainExpertRole(req: Request, res: Response, next: NextFunction) {
    const user = req.user;
    
    if (!user) {
        return res.status(401).json(createErrorResponse(
            'Authentication Required',
            'User authentication required for domain operations',
            401
        ));
    }

    const allowedRoles = ['domain-expert', 'architect', 'admin'];
    const hasPermission = allowedRoles.some(role => user.roles?.includes(role));
    
    if (!hasPermission) {
        return res.status(403).json(createErrorResponse(
            'Insufficient Permissions',
            'Domain expert or architect role required for this operation',
            403
        ));
    }

    next();
}

/**
 * Rate Limiting Middleware for DDD Operations
 * 
 * Prevents abuse of design operations
 */
export function rateLimitDomainOperations(
    windowMs: number = 15 * 60 * 1000, // 15 minutes
    maxRequests: number = 100
) {
    const requests = new Map<string, { count: number; resetTime: number }>();
    
    return (req: Request, res: Response, next: NextFunction) => {
        const clientId = req.user?.id || (req.ip as string) || 'unknown';
        const now = Date.now();
        const windowStart = now - windowMs;
        
        // Clean up old entries
        for (const [key, value] of requests.entries()) {
            if (value.resetTime < windowStart) {
                requests.delete(key);
            }
        }
        
        const clientRequests = requests.get(clientId);
        
        if (!clientRequests) {
            requests.set(clientId, { count: 1, resetTime: now });
            return next();
        }
        
        if (clientRequests.resetTime < windowStart) {
            requests.set(clientId, { count: 1, resetTime: now });
            return next();
        }
        
        if (clientRequests.count >= maxRequests) {
            return res.status(429).json(createErrorResponse(
                'Rate Limit Exceeded',
                'Too many domain operations. Please try again later.',
                429
            ));
        }
        
        clientRequests.count++;
        next();
    };
}